import Breadcrumbs from "@/components/Breadcrumbs";
import { PageService, SettingsService } from "@/services/admin-services";
import { Page } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale, UseCase } from "@/services/global-services/models";
import { ArchiveBoxArrowDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const breadcrumbs = [
    {
        to: "../../..",
        title: "Administration",
    },
    {
        title: "Settings",
        to: "../../..",
    },
    {
        title: "Use-cases",
        to: "..",
    },
];

type NestedPage = Page & {
    level: number;
};

const Details = () => {
    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);
    const [data, setData] = useState<UseCase>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [languages, setLanguages] = useState<Array<Language>>([]);
    const [pages, setPages] = useState<NestedPage[]>([]);

    const [notification, setNotification] = useState<boolean>(false);
    const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);

    const settingsService = useMemo(() => new SettingsService("int"), []);
    const pageService = useMemo(() => new PageService("int"), []);

    const { identifier, language } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            settingsService.getUseCases(language!).then((data) => {
                const useCase = data.find((x) => x.id === identifier);
                setBreadcrumbTrail([
                    ...breadcrumbs,
                    { title: useCase ? `${useCase.code} - ${useCase.name}` : "Not found", to: "" },
                ]);
                setData(useCase!);
            }),
            LocaleService.get().then(parseLanguages),
            pageService.getPages().then(nestPages),
        ]).then(() => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!notification) return;

        notificationTimerRef.current = setTimeout(() => hideNotification(), 3000);
    }, [notification]);

    useEffect(() => {
        if (!data) return;

        const language = languages.find((x) => x.slug === identifier);
        if (!language) return;

        setBreadcrumbTrail([...breadcrumbs, { title: `${language.label}`, to: "" }]);
    }, [data, languages, identifier]);

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const hideNotification = () => setNotification(false);

    const onCancel = () => navigate("..");

    const onSave = () => {
        if (!data) return;

        setIsSaving(true);

        settingsService.updateUseCaseLink(identifier!, language!, data.internalLink ?? "").then(() => {
            setNotification(true);
            setIsSaving(false);
        });
    };

    const nestPages = (pages: Page[], nested: NestedPage[] = [], parent?: string, indent: number = 0) => {
        pages
            .filter((x) => x.language === language! && x.parentPageId === parent)
            .sort((a, b) => a.parentOrder - b.parentOrder)
            .forEach((page) => {
                nested.push({ ...page, level: indent });
                nestPages(pages, nested, page.id, indent + 1);
            });

        if (indent > 0) return;

        setPages(nested);
    };

    if (isLoading || !data)
        return (
            <Box>
                <Breadcrumbs trail={breadcrumbTrail} />

                <Divider />
                <Box m={4} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 500 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbTrail} />

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Box sx={{ width: "100%" }}>
                    <Collapse in={notification}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => hideNotification()}
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </IconButton>
                            }
                            severity={"success"}
                        >
                            Use-case updated successfully.
                        </Alert>
                    </Collapse>
                </Box>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={700}>
                        Use-case
                    </Typography>
                    <Typography variant="subtitle2" color={"text.secondary"}>
                        Configure the use-case details.
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                'Read more' link
                            </Typography>
                            <FormControl fullWidth>
                                <InputLabel id="demo-select-small-2">Link</InputLabel>
                                <Select
                                    labelId="demo-select-small-2"
                                    id="demo-select-small-2"
                                    value={data.internalLink || ""}
                                    label="Link"
                                    onChange={(e) => {
                                        setData({
                                            ...data,
                                            internalLink: e.target.value,
                                        });
                                    }}
                                >
                                    <MenuItem value="">
                                        <Box sx={{ ml: 2 }}>No link</Box>
                                    </MenuItem>
                                    {pages.map((page) => (
                                        <MenuItem key={page.id} value={page.id}>
                                            <Box sx={{ ml: page.level * 2 }}>{page.title}</Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ display: "flex", alignContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{ ml: "auto" }}
                                onClick={() => onCancel()}
                                disabled={Boolean(isSaving)}
                            >
                                Cancel
                            </Button>

                            <Box sx={{ ml: 1, position: "relative" }}>
                                <Button
                                    variant="contained"
                                    startIcon={<ArchiveBoxArrowDownIcon className="h-5 w-5" />}
                                    sx={{ ml: 1 }}
                                    onClick={() => onSave()}
                                    disabled={Boolean(isSaving)}
                                >
                                    Save
                                </Button>
                                {isSaving && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Divider />
        </Box>
    );
};

export default Details;
